import * as blueprint from '@adc/blueprint'
import { match } from 'ts-pattern'
import type { Listing } from '@adc/sdk/types'

export type PathType = 'privateSeller' | 'foreclosure'

// Purchase-path metadata keyed by type. Declaration order is display order —
// add an entry to support another disposition path.
export const PATH_CONFIG: Record<
  PathType,
  {
    title: string
    subtitle: string
    elmId: string
    analyticsPath: string
    iconName: string
    color: 'purple' | 'success'
  }
> = {
  privateSeller: {
    title: 'Make an offer to the Owner today',
    subtitle: 'Negotiate directly with the Owner',
    elmId: 'make-an-offer',
    analyticsPath: 'private_seller',
    iconName: 'messages-dollar',
    color: 'purple',
  },
  foreclosure: {
    title: 'Prepare for the Foreclosure Auction',
    subtitle: 'Bid at the upcoming auction',
    elmId: 'bid-at-foreclosure',
    analyticsPath: 'foreclosure',
    iconName: 'gavel',
    color: 'success',
  },
}

// Whether a listing still renders a public PDP a buyer can act on. Canonical
// definition lives in blueprint (listing.utils) so the PDP UI and the server
// cycle selection share one source of truth; re-exported here for existing
// consumers.
export const isPubliclyDisplayable = (listing: Listing): boolean =>
  blueprint.listing.utils.isPubliclyDisplayable(listing)

const getPathType = (listing: Listing): PathType | undefined =>
  match(listing)
    .when(blueprint.listing.utils.isForeclosure, () => 'foreclosure' as const)
    .when(
      blueprint.listing.utils.isPrivateSeller,
      () => 'privateSeller' as const
    )
    .otherwise(() => undefined)

export interface PurchasePath {
  type: PathType
  listing: Listing
}

// Purchase paths for a property's active listings, in PATH_CONFIG display
// order. Active listings arrive already scoped by the graph.
export const getPurchasePaths = (activeListings?: Listing[]): PurchasePath[] =>
  (Object.keys(PATH_CONFIG) as PathType[]).flatMap(type => {
    // A listing that no longer renders a public PDP (canceled foreclosure,
    // withdrawn private-seller offering...) is not a purchase path — offering
    // it would dead-end the buyer on the "property no longer available" page.
    const listing = activeListings
      ?.filter(isPubliclyDisplayable)
      .find(l => getPathType(l) === type)
    return listing ? [{ type, listing }] : []
  })
